import { AvailableTabs } from 'constants/SidebarTabs';
import { HostBase } from 'lib/hosts/interface';
import { McleodEnterprise } from 'lib/hosts/tms/mcleodenterprise';
import { QuantumEdge } from 'lib/hosts/tms/quantumedge';
import { NormalizedLoad } from 'types/Load';
import { Maybe } from 'types/UtilityTypes';
import { TMS } from 'types/enums/Integrations';
import captureException from 'utils/captureException';

import { Aljex } from './aljex';
import { GlobalTranz } from './globaltranz';
import { MercuryGateBoomi } from './mercurygateboomi';
import { Relay } from './relay';
import { Revenova } from './revenova';
import { Stark } from './stark';
import { Tai } from './tai';
import { ThreeG } from './threeg';
import { TransportPro } from './transportpro';
import { Turvo } from './turvo';

export interface TMSHost extends HostBase {
  /**
   * The name of the field to display in the load details sidebar. For example, for Relay, load.ExternalTMSID is "Load #",
   * and FreightTrackingID is "Booking ID" and is the ID commonly shared in communications with carriers.
   * @param key - The key of the field to display in the load details sidebar.
   * @returns The name of the field to display in the load details sidebar. Should return empty string for 'externalTMSID' if
   * the field is not supported/the same as FreightTrackingID in the TMS.
   */
  getFieldMoniker(
    key: keyof Pick<NormalizedLoad, 'externalTMSID' | 'freightTrackingID'>
  ): string;

  /**
   * Parse the freight tracking ID from the web page so Drumkit can display it in the sidebar.
   * Freight Tracking ID is the ID users commonly use to identify the load.
   * @returns The freight tracking ID
   */
  parseFreightTrackingID(): string;

  /**
   * Parse the external TMS ID from the web page so Drumkit can display it in the sidebar.
   * ExternalTMSID is the UUID of the load in the TMS. Sometimes this is the same as FreightTrackingID (Aljex, McleodEnterprise)
   * but sometimes it is not (Turvo Custom ID vs. UUID, Relay Load # vs. Booking ID).
   * In the cases where they are the same, parseExternalTMSID can simply return parseFreightTrackingID().
   */
  parseExternalTMSID(): string;

  /**
   * Determine the default tab to open in the load details sidebar. For example, on the Relay planning board
   * Drumkit automatically opens the appointment scheduling tab, but on the tracking board,
   * it opens the track & trace tab.
   */
  determineDefaultLoadTab(): AvailableTabs;
}

export function newTMSHost(host: TMS): Maybe<TMSHost> {
  if (!host) return null;

  try {
    return tmsToDrumkitHostInstanceMap[host];
  } catch (error: any) {
    captureException(error, { host, functionName: 'newTMSHost' });
    return null;
  }
}

/**
 * Determines the TMS host based on the current URL.
 * @returns The TMS host, or null if no match is found
 */
export function determineTMSHost(): Maybe<TMS> {
  const url = location.href;

  for (const [tms, patterns] of Object.entries(tmsToUrlPatternMap)) {
    if (patterns) {
      for (const pattern of patterns) {
        if (url.includes(pattern)) {
          return tms as TMS;
        }
      }
    }
  }

  return null;
}

// Record to enforce new TMS enums are handled
const tmsToDrumkitHostInstanceMap: Record<NonNullable<TMS>, Maybe<TMSHost>> = {
  [TMS.Aljex]: new Aljex(),
  [TMS.Turvo]: new Turvo(),
  [TMS.Tai]: new Tai(),
  [TMS.Relay]: new Relay(),
  [TMS.McleodEnterprise]: new McleodEnterprise(),
  [TMS.QuantumEdge]: new QuantumEdge(),
  [TMS.Revenova]: new Revenova(),
  [TMS.Stark]: new Stark(),
  [TMS.GlobalTranzTMS]: new GlobalTranz(),
  [TMS.ThreeG]: new ThreeG(),
  [TMS.MercurygateBoomi]: new MercuryGateBoomi(),

  // TODO
  [TMS.FreightFlow]: null,
  [TMS.Mcleod]: null,
  [TMS.TransportPro]: new TransportPro(),
  [TMS.PLS]: null,
};

/**
 * Record mapping TMS to URL substring patterns.
 * The compiler enforces that every TMS enum has an entry.
 * Use null for TMSes that don't have URL-based detection yet.
 */
const tmsToUrlPatternMap: Record<NonNullable<TMS>, Maybe<string[]>> = {
  [TMS.Aljex]: ['aljex.com', 'aljex.descartes.com'],
  [TMS.Turvo]: ['turvo.com'],
  [TMS.Tai]: ['taicloud.net'],
  [TMS.Relay]: ['relaytms.com'],
  [TMS.Revenova]: ['lightning.force.com'],
  [TMS.QuantumEdge]: ['edgetms.com'],
  [TMS.Stark]: ['transfix.io'],
  [TMS.GlobalTranzTMS]: ['globaltranz.com'],
  [TMS.ThreeG]: ['3gtms.com'],
  [TMS.TransportPro]: ['transportpro.net'],
  [TMS.MercurygateBoomi]: ['mercurygate.net'],

  // TODO
  [TMS.FreightFlow]: null,
  [TMS.Mcleod]: null,

  // Never supported, desktop app
  [TMS.McleodEnterprise]: null,
  [TMS.PLS]: null,
};
